import { inject, injectable } from 'inversify';
import { IMapper } from '@Mapper/IMapper';
import { ArmyListMapper } from '@Mapper/builder/army-list.mapper';
import { TYPES } from '@Types';
import {
  RequestAction,
  RequestFailure,
  RequestSuccess,
} from '../RequestAction';
import { RequestArmyListsSuccess, RequestArmyListSuccess } from './response';
import CacheRepository from '../CacheRepository';
import { ArmyList, Nullable } from '@Models';
import { army_list } from '@Api';
import { army_list_full } from '@Api';
import { ApiClient } from '@Api';
import { DeleteRepository } from '../DeleteRepository';
import { RequestArmyListVersioningSuccess } from '../ninthAge/response';

@injectable()
export class ArmyListRepository
  extends CacheRepository<string, army_list_full, army_list, ArmyList>
  implements DeleteRepository<string>
{
  @inject(TYPES.ArmyListMapper) private _armyListMapper!: ArmyListMapper;
  @inject(TYPES.Api.ApiClient) private _apiClient!: ApiClient;

  constructor() {
    super({ key: `/builder/army_lists` });
  }

  public async create({
    armyList,
    locale,
  }: {
    armyList: ArmyList;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const armyListRequestBody = this._armyListMapper.mapDto(armyList);

      const armyListDto = await this._apiClient.builder.createArmyList({
        locale: this._locale.getlocale(),
        requestBody: armyListRequestBody,
      });
      await this.setCachedEntity({ data: armyListDto, locale: locale });
      return new RequestArmyListSuccess({
        payload: this._armyListMapper.mapFull(armyListDto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async update({
    id,
    armyList,
    locale,
  }: {
    id: string;
    armyList: ArmyList;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const armyListRequestBody = this._armyListMapper.mapDto(armyList);

      const armyListDto = await this._apiClient.builder.updateArmyList({
        locale: this._locale.getlocale(),
        id: id,
        requestBody: armyListRequestBody,
      });
      await this.setCachedEntity({ data: armyListDto, locale: locale });
      return new RequestArmyListSuccess({
        payload: this._armyListMapper.mapFull(armyListDto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async delete({
    id,
    locale,
  }: {
    id: string;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      await this._apiClient.builder.deleteArmyList({
        locale: this._locale.getlocale(),
        id: id,
      });
      await this.removeCachedEntity({ entityId: id, locale: locale });
      return new RequestSuccess();
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async getArmyLists({
    locale,
  }: {
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const armyListsDto = await this._apiClient.builder.getArmyLists({
        locale: this._locale.getlocale(),
      });

      await this.setCachedEntities({ data: armyListsDto, locale: locale });
      return new RequestArmyListsSuccess({
        payload: armyListsDto.map((item) => this._armyListMapper.map(item)),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async getArmyList({
    id,
    locale,
  }: {
    id: string;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const armyListDto = await this._apiClient.builder.getArmyList({
        locale: locale ?? this._locale.getlocale(),
        id: id,
      });

      await this.setCachedEntity({ data: armyListDto, locale: locale });
      return new RequestArmyListSuccess({
        payload: this._armyListMapper.mapFull(armyListDto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  protected getMapper(): IMapper<string, army_list, army_list, ArmyList> {
    return this._armyListMapper;
  }

  public async getVersioning({
    locale,
  }: {
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const itemsDto = await this._apiClient.builder.getUpdatedAtArmyLists({
        locale: locale ?? this._locale.getlocale(),
      });
      return new RequestArmyListVersioningSuccess({
        payload: itemsDto.map((item) =>
          this._armyListMapper.mapVersioning(item),
        ),
      });
    } catch (error: any) {
      console.log('error', error);
      return new RequestFailure({ error: error });
    }
  }
}
